import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { ProfileFormData } from "@features/settings/model/types";

export interface UpdateProfileRequest {
  field_firstname: string;
  field_lastname: string;
  field_job_title: string;
  field_country: number[];
  field_city: string;
  field_phone_number: string;
  field_description: string | null;
  field_linkedin: string | null;
  field_expertise: number[];
  field_sectors: number[];
  user_picture: string;
}

export interface UpdateProfileResponse {
  profile_percentage: number;
  user_data: UpdateProfileRequest;
}

export interface NotificationSetting {
  machine_name: string;
  label: string;
  enabled: boolean;
}

export type NotificationSettingsResponse = NotificationSetting[];

export interface UserBasicInfo {
  uid: string;
  name: string;
  mail: string;
  created: string;
  access: string;
  login: string;
  status: string;
  timezone: string;
  language: string;
  roles?: string[];
  pro_user?: boolean;
  is_new: boolean;
  partner: string | PartnerType;
  plan: boolean | PlanType | SubscriptionResponse;
  free_trial: boolean;
  is_stripe_customer?: boolean;
}
export interface PartnerType {
  status: string;
  partner_id: string;
}
export interface PlanType {
  plan_name: string | null;
  plan_status: string | null;
}

export interface SubscriptionPeriod {
  start: string;
  end: string;
}

export interface ActiveSubscription {
  plan: string;
  status: string;
  period: SubscriptionPeriod;
  billing_date: string;
  canceled: boolean;
}

export interface SubscriptionResponse {
  subscription: ActiveSubscription | null;
}

export type ActiveSubscriptionResponse = boolean | SubscriptionResponse;

export interface BillingAddress {
  city: string;
  country: string;
  line1: string;
  postal_code: string;
}

export interface BillingCustomer {
  id: string;
  email: string;
  name: string;
  phone: string;
  address: BillingAddress;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface BillingInfo {
  customer: BillingCustomer;
  payment_methods: PaymentMethod[];
  default_payment_method: string;
}

export interface BillingInfoResponse {
  portal_url: BillingInfo;
}

export interface CheckoutLinkResponse {
  session_url: string;
}

export interface UserProfileResponse {
  profile_percentage: number;
  user_data: {
    field_firstname: string;
    field_lastname: string;
    field_job_title: string;
    field_country: string[];
    field_city: string;
    field_phone_number: string;
    field_expertise: string[];
    field_sectors: string[];
    field_linkedin: string | null;
    field_description: string | null;
    user_picture:
      | string
      | {
          fid: number;
          filename: string;
          filesize: number;
          mime: string;
          url: string;
        }[]
      | null;
  };
}

interface UpdateUserSettingsRequest {
  old_password?: string;
  new_email?: string;
  new_password?: string;
}

class UserService {
  async updateProfile(
    profileData: ProfileFormData
  ): Promise<UpdateProfileResponse> {
    // Transform form data to API format
    const updateData: UpdateProfileRequest = {
      field_firstname: profileData.firstName,
      field_lastname: profileData.lastName,
      field_job_title: profileData.jobTitle,
      field_country: profileData.country ? [profileData.country] : [],
      field_city: profileData.city,
      field_phone_number: profileData.phoneNumber,
      field_description: profileData.bio || null,
      field_linkedin: profileData.linkedin || null,
      field_expertise: profileData.expertise,
      field_sectors: profileData.sectors,
      user_picture: profileData.userPicture,
    };

    return await apiService.patch<UpdateProfileResponse>(
      API_ENDPOINTS.USER.UPDATE_PROFILE,
      updateData
    );
  }

  async updateUserSettings(data: UpdateUserSettingsRequest): Promise<unknown> {
    return await apiService.post(API_ENDPOINTS.USER.SETTINGS, data);
  }

  async getNotifications(): Promise<NotificationSettingsResponse> {
    return await apiService.get<NotificationSettingsResponse>(
      API_ENDPOINTS.USER.NOTIFICATIONS
    );
  }

  async updateNotifications(
    notifications: NotificationSetting[]
  ): Promise<NotificationSettingsResponse> {
    return await apiService.patch<NotificationSettingsResponse>(
      API_ENDPOINTS.USER.NOTIFICATIONS,
      notifications
    );
  }

  async getBasicInfo(): Promise<UserBasicInfo> {
    return await apiService.get<UserBasicInfo>(API_ENDPOINTS.USER.BASIC_INFO);
  }

  async getProfile(): Promise<UserProfileResponse> {
    return await apiService.get<UserProfileResponse>(
      API_ENDPOINTS.USER.GET_PROFILE
    );
  }

  async deleteUserAccount(): Promise<void> {
    await apiService.delete(API_ENDPOINTS.USER.SETTINGS);
  }
}

export const userService = new UserService();
